import { GridSortModel } from '@mui/x-data-grid';

import {
  AnalysisQuestionFeedback,
  AnalysisQuestionReport,
  ArchiveProjectOptions,
  CategoryProgress,
  CollectionImportResult,
  ConflictReportContent,
  CreateProjectOptions,
  DataRequestItem,
  FetchLastRunOptions,
  GetScopeQuestionsOptions,
  IntellioDataRequestItem,
  IntellioDataRequestItemSelection,
  KpiMetric,
  PageInfo,
  PatchCategoryOpts,
  PatchCollectionOpts,
  PatchDataRequestItemOpts,
  PatchQuestionOpts,
  PostCategoryOpts,
  PostCollectionOpts,
  PostQuestionOpts,
  Project,
  ProjectConfig,
  ProjectConfigQuestion,
  ProjectConfigurationType,
  ProjectIndexResult,
  ProjectNotes,
  ProjectQuestionReport,
  ProjectRun,
  ProjectRunAnalysisQuestion,
  ProjectsPageOptions,
  ProjectWithLastScanned,
  Question,
  QuestionCategory,
  QuestionCollection,
  QuestionType,
  QuestionTypeTreeNode,
  RescanProjectQuestionsOptions,
  RubricCore,
  RunProjectOptions,
  ScopePagePair,
  SOWExtractionResult,
  StartExtractionOptions,
  StepDependenciesOptions,
  SummaryAssistantResult,
  UpdateProjectOptions,
  ValidateFindingsOptions,
} from '../Types/dealGPT';
import {
  AnalysisQuestionStatus,
  DealGPTDiscoveryView,
  DealGPTQuestionStatus,
  IndexType,
  IntellioAdvTone,
  ProcessReason,
} from '../Types/enums';
import { CustomFile } from '../Types/file';
import { SourceDocument } from '../Types/indexes';
import { PaginatedResult } from '../Types/pagination';
import { doubleEncodeUriComponent } from '../util/formatterUtils';
import {
  categoryPathUrl,
  collectionPathUrl,
  collectionUploadPathUrl,
  conflictReportPathUrl,
  createCategoryPathUrl,
  createCollectionPathUrl,
  createKpiQuestionPathUrl,
  createProjectRunPathUrl,
  createQuestionPathUrl,
  dataRequestItemPathUrl,
  dataRequestListBulkPathUrl,
  dataRequestListReIndexPathUrl,
  deleteKeyQuestionPathUrl,
  deleteKpiQuestionPathUrl,
  fetchCategoryQuestionsPathUrl,
  fetchQuestionAllVersionsPathUrl,
  generateAnalysisReportPathUrl,
  generateDataRequestListReportPathUrl,
  generateDiscoveryReportPathUrl,
  investmentThesisPathUrl,
  keyQuestionRelevantDocsPathUrl,
  projectConfigPathUrl,
  projectDataProcessPathUrl,
  projectDefaultKPIsPathUrl,
  projectFolderExistsPathUrl,
  projectKpisRunPathUrl,
  projectPathUrl,
  projectQuestionRescanPathUrl,
  projectQuestionRunInclusionPathUrl,
  projectRunProgressPathUrl,
  projectsPathUrl,
  questionPathUrl,
  recalculateBenchmarksPathUrl,
  reorderCategoriesPathUrl,
  reorderQuestionsPathUrl,
  sowExtractionPathUrl,
  startProjectRunPathUrl,
  summaryChatFilesAssistantPathUrl,
  summaryChatPathUrl,
  updateProjectRunPathUrl,
} from '../util/routesUrls';
import { withRetry } from '../util/withRetry';
import { API_URL, enrichedFetch } from './api';
import { fetchTokens } from './auth';

export const getProjects = async (options: ProjectsPageOptions) =>
  withRetry(
    async () => {
      const response = await enrichedFetch(`${API_URL}/${projectsPathUrl(options)}`);
      return response as ProjectWithLastScanned[];
    },
    {
      label: 'Fetching projects',
      onRetry: fetchTokens,
    }
  );

export const getProject = (projectId: string, isAdmin = false): Promise<ProjectWithLastScanned> => {
  return enrichedFetch(
    `${API_URL}/${projectPathUrl(projectId, isAdmin)}`
  ) as Promise<ProjectWithLastScanned>;
};

export const saveProject = (projectOptions: CreateProjectOptions): Promise<Project> => {
  return enrichedFetch(`${API_URL}/dealgpt/project`, {
    method: 'POST',
    body: JSON.stringify(projectOptions),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<Project>;
};

export const editProject = (projectOptions: UpdateProjectOptions): Promise<Project> => {
  return enrichedFetch(`${API_URL}/dealgpt/project`, {
    method: 'PUT',
    body: JSON.stringify(projectOptions),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<Project>;
};

export const rebuildProjectIndex = (
  projectId: string,
  indexType: IndexType
): Promise<ProjectIndexResult> => {
  return enrichedFetch(
    `${API_URL}/dealgpt/project/${projectId}/rebuild-index/${indexType}`
  ) as Promise<ProjectIndexResult>;
};

export const deleteProjectIndex = (projectId: string, indexType: IndexType): Promise<boolean> => {
  return enrichedFetch(`${API_URL}/dealgpt/project/${projectId}/delete-index/${indexType}`, {
    method: 'DELETE',
  }) as Promise<boolean>;
};

export const getProjectConfig = (projectId: string): Promise<ProjectConfig> => {
  return enrichedFetch(`${API_URL}/${projectConfigPathUrl(projectId)}`) as Promise<ProjectConfig>;
};

export const saveProjectConfiguration = (
  projectId: string,
  config: ProjectConfigurationType
): Promise<ProjectConfigQuestion[]> => {
  return enrichedFetch(`${API_URL}/${projectConfigPathUrl(projectId)}`, {
    method: 'POST',
    body: JSON.stringify(config),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<ProjectConfigQuestion[]>;
};

export const deleteProjectConfigQuestion = (projectId: string, questionId: string) => {
  return enrichedFetch(`${API_URL}/${deleteKeyQuestionPathUrl(projectId, questionId)}`, {
    method: 'DELETE',
  });
};

export const getKeyQuestionRelevantDocuments = async (
  projectId: string,
  questionId: string,
  runId: string
) =>
  enrichedFetch(
    `${API_URL}/${keyQuestionRelevantDocsPathUrl(projectId, questionId, runId)}`
  ) as Promise<SourceDocument[]>;

export const updateProjectInvestmentThesis = (projectId: string, investmentThesis: string) => {
  return enrichedFetch(`${API_URL}/${investmentThesisPathUrl(projectId)}`, {
    method: 'PUT',
    body: JSON.stringify({ investmentThesis }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

const KPI_PROMPT = `Format the response like the following json:
{
    ""value"": string;
    ""findings"": string;
}
If you cannot find the answer or there is no mention of the answer in the documents, make sure your response is an empty string. Question is the question I asked you to answer, findings is your reasoning for the value you picked, and value must be text. If a value cannot be found, LEAVE IT EMPTY.

Always share the source in which you think the answer may be found or whether you find the answer in the site visit notes. Use all available documents and the site visit notes for this project. Answer the question: "`;

export const insertProjectKpiQuestion = (
  question: ProjectConfigQuestion,
  runId: string,
  displayOrder: number
): Promise<ProjectRunAnalysisQuestion> => {
  return enrichedFetch(`${API_URL}/${createKpiQuestionPathUrl(question.projectId)}`, {
    method: 'POST',
    body: JSON.stringify({
      runId: runId === undefined ? '' : runId,
      status: 'INITIAL',
      starred: false,
      prompt: KPI_PROMPT + question.question,
      prettyQuestion: question.question,
      scope: 'PROJECT',
      area: 'PROJECT',
      dimension: 'PROJECT',
      pageName: 'KPI',
      type: 'KPI',
      kpiType: question.kpiType,
      sequence: 0,
      displayOrder,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<ProjectRunAnalysisQuestion>;
};

export const updateProjectKpiStatus = (
  projectId: string,
  question: ProjectRunAnalysisQuestion,
  status: AnalysisQuestionStatus
): Promise<ProjectRunAnalysisQuestion> => {
  return enrichedFetch(`${API_URL}/${createKpiQuestionPathUrl(projectId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ id: question.id, status }),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<ProjectRunAnalysisQuestion>;
};

export const deleteProjectKPI = async (projectId: string, questionId: string) => {
  return enrichedFetch(`${API_URL}/${deleteKpiQuestionPathUrl(projectId, questionId)}`, {
    method: 'DELETE',
  });
};

export const archiveProject = (
  projectId: string,
  options: ArchiveProjectOptions
): Promise<boolean> => {
  return enrichedFetch(`${API_URL}/dealgpt/project/${projectId}/archive`, {
    method: 'PATCH',
    body: JSON.stringify(options),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<boolean>;
};

export const createRun = (projectId: string, options: RunProjectOptions): Promise<ProjectRun> => {
  return enrichedFetch(`${API_URL}/${createProjectRunPathUrl(projectId)}`, {
    method: 'POST',
    body: JSON.stringify(options),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<ProjectRun>;
};

export const updateRunQuestions = (
  projectId: string,
  runId: string,
  options: RunProjectOptions
): Promise<void> => {
  return enrichedFetch(`${API_URL}/${updateProjectRunPathUrl(projectId, runId)}`, {
    method: 'PUT',
    body: JSON.stringify(options),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<void>;
};

export const startRun = (projectId: string, runId: string): Promise<void> => {
  return enrichedFetch(`${API_URL}/${startProjectRunPathUrl(projectId, runId)}`, {
    method: 'POST',
  }) as Promise<void>;
};

export const fetchCategoryQuestions = (
  projectId: string,
  scopeName: string,
  categoryName: string,
  statuses: DealGPTQuestionStatus[],
  searchQuery: string,
  runId: string
): Promise<QuestionTypeTreeNode[]> => {
  return enrichedFetch(
    `${API_URL}/${fetchCategoryQuestionsPathUrl(
      projectId,
      doubleEncodeUriComponent(scopeName),
      doubleEncodeUriComponent(categoryName),
      statuses,
      searchQuery,
      runId
    )}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  ) as Promise<QuestionTypeTreeNode[]>;
};

export const getRunProgress = (
  projectId: string,
  runId: string,
  scopes: string[],
  statuses: DealGPTQuestionStatus[],
  searchQuery: string
): Promise<CategoryProgress[]> => {
  return enrichedFetch(
    `${API_URL}/${projectRunProgressPathUrl(projectId, runId, scopes, statuses, searchQuery)}`
  ) as Promise<CategoryProgress[]>;
};

export const generateDiscoveryReport = (
  projectId: string,
  reportType: DealGPTDiscoveryView,
  scopes: string[],
  statuses: DealGPTQuestionStatus[],
  selectedRunId?: string
): Promise<ProjectQuestionReport[]> => {
  return enrichedFetch(
    `${API_URL}/${generateDiscoveryReportPathUrl(
      projectId,
      reportType,
      scopes,
      statuses,
      selectedRunId
    )}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  ) as Promise<ProjectQuestionReport[]>;
};

export const generateDataRequestListReport = (
  projectId: string,
  scopes: string[],
  sortModel: GridSortModel
): Promise<PaginatedResult<DataRequestItem>> => {
  return enrichedFetch(
    `${API_URL}/${generateDataRequestListReportPathUrl(projectId, scopes, sortModel)}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  ) as Promise<PaginatedResult<DataRequestItem>>;
};

export const dataRequestListReIndex = (
  projectId: string,
  indexType: string
): Promise<ProjectRun> => {
  return enrichedFetch(`${API_URL}/${dataRequestListReIndexPathUrl(projectId, indexType)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<ProjectRun>;
};

export const getAllProjectRuns = (projectId: string, runType?: string): Promise<ProjectRun[]> => {
  let url = `${API_URL}/dealgpt/project/${projectId}/runs`;
  if (runType) {
    url = url + `?runType=${runType}`;
  }
  return enrichedFetch(url) as Promise<ProjectRun[]>;
};

export const getAnalysisPages = (
  projectId: string,
  runId: string,
  scope: string
): Promise<PageInfo[]> => {
  return enrichedFetch(
    `${API_URL}/dealgpt/analysis/pages/${projectId}/${runId}/${scope}`
  ) as Promise<PageInfo[]>;
};

export const getLatestRun = (options: FetchLastRunOptions): Promise<ProjectRun> => {
  const { projectId, scope, pageName, runType } = options;

  return enrichedFetch(
    `${API_URL}/dealgpt/analysis/runs/${projectId}/${scope}/${pageName}/${runType}`
  ) as Promise<ProjectRun>;
};

export const getScopeQuestions = (
  options: GetScopeQuestionsOptions
): Promise<ProjectRunAnalysisQuestion[]> => {
  const { projectId, scope, pageName, runId } = options;

  return enrichedFetch(
    `${API_URL}/dealgpt/analysis/questions/${projectId}/${scope}/${pageName}/${runId}`
  ) as Promise<ProjectRunAnalysisQuestion[]>;
};

export const getRunStatus = (projectId: string, scope: string, runId: string): Promise<string> => {
  return enrichedFetch(
    `${API_URL}/dealgpt/analysis/run-status/${projectId}/${scope}/${runId}`
  ) as Promise<string>;
};

export const saveNotes = (
  notes: ProjectNotes,
  reprocess = false,
  runId?: string
): Promise<ProjectNotes> => {
  return enrichedFetch(`${API_URL}/dealgpt/analysis/notes?reprocess=${reprocess}`, {
    method: 'POST',
    body: JSON.stringify({ notes, runId }),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<ProjectNotes>;
};

export const getNotes = (projectId: string, scope: string): Promise<ProjectNotes | undefined> => {
  return enrichedFetch(`${API_URL}/dealgpt/analysis/notes/${projectId}/${scope}`) as Promise<
    ProjectNotes | undefined
  >;
};

export const checkDependencies = (
  options: ProjectRunAnalysisQuestion
): Promise<StepDependenciesOptions> => {
  return enrichedFetch(`${API_URL}/dealgpt/analysis/check-step-dependencies`, {
    method: 'POST',
    body: JSON.stringify(options),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<StepDependenciesOptions>;
};

export const modifyTone = (
  tone: IntellioAdvTone,
  question: ProjectRunAnalysisQuestion
): Promise<void> => {
  return enrichedFetch(`${API_URL}/dealgpt/analysis/modify-tone/${tone}`, {
    method: 'POST',
    body: JSON.stringify(question),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<void>;
};

export const softDeleteQuestion = (
  deletedQuestion: ProjectRunAnalysisQuestion
): Promise<boolean> => {
  return enrichedFetch(`${API_URL}/dealgpt/analysis/soft-delete-question`, {
    method: 'POST',
    body: JSON.stringify(deletedQuestion),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<boolean>;
};

export const processQuestion = (
  question: ProjectRunAnalysisQuestion,
  processReason?: ProcessReason,
  overwritePrompt?: boolean
): Promise<void> => {
  let url = `${API_URL}/dealgpt/analysis/process-response`;

  if (processReason) {
    url = `${url}?processReason=${processReason}`;
  }

  if (overwritePrompt) {
    url = `${url}${processReason ? '&' : '?'}overwritePrompt=true`;
  }

  return enrichedFetch(url, {
    method: 'POST',
    body: JSON.stringify(question),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<void>;
};

export const userEditSave = (
  question: ProjectRunAnalysisQuestion
): Promise<ProjectRunAnalysisQuestion> => {
  return enrichedFetch(`${API_URL}/dealgpt/analysis/user-edit-save`, {
    method: 'POST',
    body: JSON.stringify(question),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<ProjectRunAnalysisQuestion>;
};

export const updateAnalysisQuestionFiles = (
  questionIds: string[],
  fileIds: string[]
): Promise<ProjectRunAnalysisQuestion[]> => {
  return enrichedFetch(`${API_URL}/dealgpt/analysis/questions/files`, {
    method: 'PUT',
    body: JSON.stringify({ questionIds, fileIds }),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<ProjectRunAnalysisQuestion[]>;
};

export const getProjectTypes = (): Promise<string[]> => {
  return enrichedFetch(`${API_URL}/dealgpt/project/project-types`) as Promise<string[]>;
};

export const validateFindings = (
  options: ValidateFindingsOptions,
  questionId: string,
  projectId: string
): Promise<QuestionType> => {
  return enrichedFetch(
    `${API_URL}/dealgpt/project/${projectId}/question/${questionId}/validation`,
    {
      method: 'POST',
      body: JSON.stringify(options),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  ) as Promise<QuestionType>;
};

export const validateFindingsAnalysis = (
  options: ValidateFindingsOptions,
  questionId: string,
  projectId: string
): Promise<ProjectRunAnalysisQuestion> => {
  return enrichedFetch(
    `${API_URL}/dealgpt/analysis/project/${projectId}/question/${questionId}/validation`,
    {
      method: 'POST',
      body: JSON.stringify(options),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  ) as Promise<ProjectRunAnalysisQuestion>;
};

export const rescanQuestions = (options: RescanProjectQuestionsOptions): Promise<void> => {
  return enrichedFetch(`${API_URL}/${projectQuestionRescanPathUrl()}`, {
    method: 'POST',
    body: JSON.stringify(options),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<void>;
};

export const updateAnalysisQuestionFeedback = (
  projectAnalysisQuestionId: string,
  analysisQuestionFeedback: AnalysisQuestionFeedback
): Promise<void> => {
  return enrichedFetch(
    `${API_URL}/dealgpt/analysis/questionAnalysisFeedback/${projectAnalysisQuestionId}`,
    {
      method: 'PUT',
      body: JSON.stringify({ analysisQuestionFeedback }),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  ) as Promise<void>;
};

export const getRubricCore = (scope: string): Promise<RubricCore[]> => {
  return enrichedFetch(`${API_URL}/dealgpt/analysis/rubric-core/${scope}`) as Promise<RubricCore[]>;
};

export const checkProjectFolderExists = (
  teamsId: string,
  channelId?: string,
  sharepointFolderURL?: string
): Promise<boolean> => {
  return enrichedFetch(
    `${API_URL}/${projectFolderExistsPathUrl(teamsId, channelId, sharepointFolderURL)}`
  ) as Promise<boolean>;
};

export const generateAnalysisReport = (
  projectId: string,
  scope: string,
  runId: string
): Promise<AnalysisQuestionReport[]> => {
  return enrichedFetch(
    `${API_URL}/${generateAnalysisReportPathUrl(projectId, scope, runId)}`
  ) as Promise<AnalysisQuestionReport[]>;
};

export const fetchQuestionAllVersions = (
  questionId: string,
  statuses: DealGPTQuestionStatus[]
): Promise<QuestionTypeTreeNode[]> => {
  return enrichedFetch(`${API_URL}/${fetchQuestionAllVersionsPathUrl(questionId, statuses)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<QuestionTypeTreeNode[]>;
};

export const syncProjectDefaultKPIs = (projectId: string): Promise<void> => {
  return enrichedFetch(`${API_URL}/${projectDefaultKPIsPathUrl(projectId)}`, {
    method: 'POST',
  }) as Promise<void>;
};

export const createCollection = (collection: PostCollectionOpts) => {
  return enrichedFetch(`${API_URL}/${createCollectionPathUrl()}`, {
    method: 'POST',
    body: JSON.stringify(collection),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<QuestionCollection>;
};

export const uploadCollection = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return enrichedFetch(`${API_URL}/${collectionUploadPathUrl()}`, {
    method: 'POST',
    body: formData,
  }) as Promise<CollectionImportResult>;
};

export const updateCollection = (collection: PatchCollectionOpts) => {
  return enrichedFetch(`${API_URL}/${collectionPathUrl(collection.id)}`, {
    method: 'PATCH',
    body: JSON.stringify(collection),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<QuestionCollection>;
};

export const createCategory = (category: PostCategoryOpts) => {
  return enrichedFetch(`${API_URL}/${createCategoryPathUrl()}`, {
    method: 'POST',
    body: JSON.stringify(category),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<QuestionCategory>;
};

export const updateCategory = (category: PatchCategoryOpts) => {
  return enrichedFetch(`${API_URL}/${categoryPathUrl(category.id)}`, {
    method: 'PATCH',
    body: JSON.stringify(category),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<QuestionCategory>;
};

export const createQuestion = (question: PostQuestionOpts) => {
  return enrichedFetch(`${API_URL}/${createQuestionPathUrl()}`, {
    method: 'POST',
    body: JSON.stringify(question),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<QuestionCollection>;
};

export const updateQuestion = (question: PatchQuestionOpts) => {
  return enrichedFetch(`${API_URL}/${questionPathUrl(question.id)}`, {
    method: 'PATCH',
    body: JSON.stringify(question),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<Question>;
};

export const reorderQuestions = (orderedQuestions: Question[]) => {
  return enrichedFetch(`${API_URL}/${reorderQuestionsPathUrl()}`, {
    method: 'PUT',
    body: JSON.stringify({ orderedIds: orderedQuestions.map((q) => q.id) }),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<void>;
};

export const reorderCategories = (orderedCategories: QuestionCategory[]) => {
  return enrichedFetch(`${API_URL}/${reorderCategoriesPathUrl()}`, {
    method: 'PUT',
    body: JSON.stringify({ orderedIds: orderedCategories.map((q) => q.id) }),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<void>;
};

export const deleteQuestion = (questionId: string) => {
  return enrichedFetch(`${API_URL}/${questionPathUrl(questionId)}`, {
    method: 'DELETE',
  }) as Promise<Question>;
};

export const deleteCategory = (categoryId: string) => {
  return enrichedFetch(`${API_URL}/${categoryPathUrl(categoryId)}`, {
    method: 'DELETE',
  }) as Promise<QuestionCategory>;
};

export const deleteCollection = (collectionId: string) => {
  return enrichedFetch(`${API_URL}/${collectionPathUrl(collectionId)}`, {
    method: 'DELETE',
  }) as Promise<QuestionCollection>;
};

export const updateProjectQuestionRunInclusion = (questionId: string, includeInRun: boolean) => {
  return enrichedFetch(`${API_URL}/${projectQuestionRunInclusionPathUrl(questionId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ includeInRun }),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<QuestionType>;
};

export const sowExtraction = async (
  file: File,
  projectId: string
): Promise<SOWExtractionResult> => {
  const formData = new FormData();
  formData.append('file', file);
  return enrichedFetch(`${API_URL}/${sowExtractionPathUrl(projectId)}`, {
    method: 'POST',
    body: formData,
  }) as Promise<SOWExtractionResult>;
};

export const generateConflictReport = async (projectId: string): Promise<ConflictReportContent> => {
  return enrichedFetch(`${API_URL}/${conflictReportPathUrl(projectId)}`, {
    method: 'POST',
  }) as Promise<ConflictReportContent>;
};

export const recalculateBenchmarks = async (projectId: string, runId: string): Promise<void> => {
  return enrichedFetch(`${API_URL}/${recalculateBenchmarksPathUrl(projectId, runId)}`, {
    method: 'POST',
  }) as Promise<void>;
};

function sanitizeFile(file: CustomFile): Partial<CustomFile> {
  const sanitized: Partial<CustomFile> = { ...file };

  const optionalStringFields: (keyof CustomFile)[] = [
    'folderId',
    'promptId',
    'encoding',
    'contents',
    'userId',
    'status',
    'vectorStoreStatus',
    'openaiId',
    'sharepointId',
    'relativePath',
    'lastModified',
    'url',
    'indexId',
  ];

  for (const field of optionalStringFields) {
    if (sanitized[field] === null) {
      delete sanitized[field];
    }
  }

  return sanitized;
}

export const startDataExtraction = (
  projectId: string,
  selectedFiles: StartExtractionOptions
): Promise<string> => {
  const sanitizedFiles = selectedFiles.files.map(sanitizeFile);

  const options = { files: sanitizedFiles };
  return enrichedFetch(`${API_URL}/${projectDataProcessPathUrl(projectId)}`, {
    method: 'POST',
    body: JSON.stringify(options),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<string>;
};

export const updateKpisForRun = (
  projectId: string,
  runId: string,
  kpis: KpiMetric[]
): Promise<void> => {
  return enrichedFetch(`${API_URL}/${projectKpisRunPathUrl(projectId, runId)}`, {
    method: 'PUT',
    body: JSON.stringify(kpis),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<void>;
};

export const editDataRequestItem = (
  projectId: string,
  body: PatchDataRequestItemOpts
): Promise<DataRequestItem> => {
  return enrichedFetch(`${API_URL}/${dataRequestItemPathUrl(projectId, body.id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<DataRequestItem>;
};

export const bulkEditDataRequestItems = async (
  projectId: string,
  selections: IntellioDataRequestItemSelection[]
): Promise<void> => {
  const payload: IntellioDataRequestItem[] = selections.map(
    ({ selected: _selected, ...item }) => item
  );

  return enrichedFetch(`${API_URL}/${dataRequestListBulkPathUrl(projectId)}`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-Type': 'application/json',
    },
  }) as Promise<void>;
};

export const createSummaryChat = (
  projectId: string,
  options: { collectionIds?: string[]; scopePagePairs?: ScopePagePair[] }
): Promise<SummaryAssistantResult> => {
  return enrichedFetch(`${API_URL}/${summaryChatPathUrl(projectId)}`, {
    method: 'POST',
    body: JSON.stringify(options),
    headers: { 'Content-Type': 'application/json' },
  }) as Promise<SummaryAssistantResult>;
};

export const getAssistantIdByVectorStoreId = (vectorStoreId: string): Promise<string> => {
  return enrichedFetch(
    `${API_URL}/${summaryChatFilesAssistantPathUrl(vectorStoreId)}`
  ) as Promise<string>;
};
